import {
  build_case_gate_journey,
  resolve_gate_id_from_stage_label
} from '@/services/onboarding_journey_service';

const APPLICATION_REFERENCE_TAB_ID = 'application-reference';
import { first_value } from '@/services/onboarding_form_helpers';

const STAFF_PAGE_SUBTITLE = 'Complete the assigned onboarding form for this applicant.';
const APPLICANT_PAGE_SUBTITLE = 'Complete your assigned onboarding forms.';
const DEFAULT_SECTION_SUBTITLE = 'Complete the required details for this section before moving to the next step.';

function build_traversed_workflow_gate_ids(transition_history = []) {
  const logs = Array.isArray(transition_history) ? [...transition_history] : [];
  if (!logs.length) return [];

  const ordered_logs = logs.sort((left, right) => (
    new Date(left?.date_created || 0).getTime() - new Date(right?.date_created || 0).getTime()
  ));
  const gate_ids = [];
  const append_gate_id = function append_gate_id(status_name = '') {
    const gate_id = resolve_gate_id_from_stage_label(status_name);
    if (gate_id && !gate_ids.includes(gate_id)) {
      gate_ids.push(gate_id);
    }
  };

  append_gate_id(ordered_logs[0]?.from_status?.name || '');
  ordered_logs.forEach((log) => {
    append_gate_id(log?.to_status?.name || '');
  });
  return gate_ids;
}

export const queueFormPageComputed = {
  applicant_name() { return String(this.$route.query.name || '').trim(); },
  company_name() { return String(this.$route.query.company || '').trim(); },
  selected_entity_type_label() {
    const route_journey = String(this.$route.query.journey || '').trim();
    const form_journey = String(this.selected_application_form_meta?.journey || '').trim();
    const form_entity_type = String(this.selected_application_form_meta?.entity_type_name || '').trim();
    return route_journey || form_journey || form_entity_type;
  },
  is_applicant_role() { return String(this.$store.getters.currentRole || '').trim().toLowerCase() === 'entity'; },
  page_eyebrow() { return this.is_applicant_role ? 'My onboarding' : 'My Queue'; },
  is_documents_gate_flow() { return String(this.$route.query.source || '').trim() === 'documents-gate'; },
  page_title() {
    if (this.is_applicant_role && this.selected_entity_type_label) return this.selected_entity_type_label;
    return this.applicant_name || 'Assigned onboarding form';
  },
  page_subtitle() {
    if (this.is_applicant_role && this.applicant_name) return `${APPLICANT_PAGE_SUBTITLE} Applicant: ${this.applicant_name}.`;
    if (this.is_applicant_role) return APPLICANT_PAGE_SUBTITLE;
    return STAFF_PAGE_SUBTITLE;
  },
  workflow_journey_steps() {
    if (this.hydrated_case_detail) {
      const current_gate_id = resolve_gate_id_from_stage_label(
        this.hydrated_case_detail?.workflow_display_stage
        || this.hydrated_case_detail?.current_workflow_status
        || this.hydrated_case_detail?.stage
        || this.hydrated_case_detail?.detail
      );
      const filtered_steps = this.filter_case_journey_steps(
        build_case_gate_journey(this.hydrated_case_detail, current_gate_id),
        current_gate_id
      );
      const current_index = filtered_steps.findIndex((step) => step.gate_id === current_gate_id);
      const traversed_gate_ids = new Set(this.traversed_workflow_gate_ids);

      return filtered_steps.map((step, index) => ({
        ...step,
        state: step.gate_id === current_gate_id
          ? 'current'
          : (current_index >= 0 && index < current_index && (
            String(step?.state || '').trim().toLowerCase() === 'completed'
            || traversed_gate_ids.has(step.gate_id)
          ))
            ? 'completed'
          : (current_index >= 0 && index < current_index)
              ? 'previous'
              : 'upcoming'
      }));
    }

    const timeline = Array.isArray(this.workflow_stage_timeline) ? this.workflow_stage_timeline : [];
    return timeline.map((entry, index) => ({
      id: `workflow-stage-${index}`,
      gate_id: `workflow-stage-${index}`,
      label: entry?.title || `Stage ${index + 1}`,
      detail_route_name: '',
      state: String(entry?.status || '').trim().toLowerCase() === 'done'
        ? 'completed'
        : String(entry?.status || '').trim().toLowerCase() === 'current'
          ? 'current'
          : 'upcoming'
    }));
  },
  traversed_workflow_gate_ids() {
    return build_traversed_workflow_gate_ids(this.workflow_transition_history);
  },
  current_workflow_step() {
    return this.workflow_journey_steps.find((step) => step.state === 'current') || null;
  },
  current_workflow_step_index() {
    return this.workflow_journey_steps.findIndex((step) => step.state === 'current');
  },
  is_initial_workflow_gate() {
    return this.current_workflow_step_index <= 0;
  },
  should_show_workflow_navigation() {
    return this.workflow_journey_steps.length > 0 && !this.is_initial_workflow_gate;
  },
  workflow_content_tabs() {
    const current_index = this.current_workflow_step_index;
    if (current_index < 0) {
      return this.workflow_journey_steps.filter((step) => step.state !== 'upcoming');
    }

    const current_step = this.current_workflow_step
      ? {
          ...this.current_workflow_step,
          label: `Required Action: ${this.current_workflow_step.label}`,
          badge: 'Current'
        }
      : null;
    const application_reference_tab = {
      id: APPLICATION_REFERENCE_TAB_ID,
      gate_id: APPLICATION_REFERENCE_TAB_ID,
      label: 'Application Reference',
      state: 'reference',
      badge: 'Reference'
    };
    const previous_steps = this.workflow_journey_steps
      .slice(0, current_index)
      .map((step) => ({
        ...step,
        badge: step.state === 'completed' ? 'Completed' : 'Previous'
      }));

    return [
      ...(current_step ? [current_step] : []),
      application_reference_tab,
      ...previous_steps
    ];
  },
  active_workflow_tab_step() {
    if (this.active_history_gate_id === APPLICATION_REFERENCE_TAB_ID) {
      return this.workflow_content_tabs.find((step) => step.gate_id === APPLICATION_REFERENCE_TAB_ID) || null;
    }
    if (!this.active_history_gate_id) return this.current_workflow_step;
    return this.workflow_content_tabs.find((step) => step.gate_id === this.active_history_gate_id) || this.current_workflow_step;
  },
  is_current_workflow_tab() {
    return !this.active_history_gate_id || this.active_history_gate_id === this.current_workflow_step?.gate_id;
  },
  is_application_reference_tab() {
    return this.active_history_gate_id === APPLICATION_REFERENCE_TAB_ID;
  },
  current_workflow_content_tab() {
    return this.workflow_content_tabs[0] || null;
  },
  workflow_reference_tab() {
    return this.workflow_content_tabs.find((step) => step.gate_id === APPLICATION_REFERENCE_TAB_ID) || null;
  },
  workflow_history_tabs() {
    return this.workflow_content_tabs.filter((step) => ![this.current_workflow_content_tab?.gate_id, APPLICATION_REFERENCE_TAB_ID].includes(step.gate_id));
  },
  workflow_shell_tabs() {
    return [
      { id: 'action', label: 'Required Action', badge: 'Current' },
      { id: 'reference', label: 'Application Reference', badge: null },
      { id: 'history', label: 'Onboarding History', badge: this.workflow_history_tabs.length ? String(this.workflow_history_tabs.length) : null }
    ];
  },
  active_workflow_shell_tab() {
    if (this.is_application_reference_tab) {
      return 'reference';
    }

    if (this.active_history_step) {
      return 'history';
    }

    return 'action';
  },
  workflow_current_action_copy() {
    if (this.is_documents_gate_flow) {
      return 'Review the uploaded documents for this application, preview each file, and use the reference tab when you need the submitted form context.';
    }
    const label = String(this.current_workflow_content_tab?.label || 'Current gate').replace(/^Required Action:\s*/i, '').trim() || 'Current gate';
    return `${label} - needs your review`;
  },
  documents_gate_title() {
    return 'Uploaded Documents';
  },
  documents_gate_subtitle() {
    const total = Array.isArray(this.history_document_items) ? this.history_document_items.length : 0;
    if (!total) return 'No uploaded documents were returned for this application.';
    return `${total} uploaded document${total === 1 ? '' : 's'} for this application.`;
  },
  workflow_reference_value() {
    const application_id = String(this.$route.params.applicationId || this.hydrated_case_detail?.application_id || '').trim();
    return application_id ? `#${application_id}` : 'Open application details';
  },
  current_step_index() { return Math.max(0, this.current_step - 1); },
    current_main_section() { return this.intake_steps[this.current_step_index] || null; },
    current_step_label() { return `Step ${this.current_step} of ${this.intake_steps.length || 1}`; },
    card_title() { return this.current_main_section?.label || this.selected_application_form_meta?.form_name || 'Assigned onboarding form'; },
    card_subtitle() { return this.current_main_section?.description || DEFAULT_SECTION_SUBTITLE; },
    current_form_instructions() {
      const instructions = Array.isArray(this.selected_application_form_detail?.form?.form_instructions)
        ? this.selected_application_form_detail.form.form_instructions
        : Array.isArray(this.selected_application_form_detail?.form_instructions)
          ? this.selected_application_form_detail.form_instructions
          : [];

      return instructions
        .map((instruction) => String(first_value([
          instruction?.content,
          instruction?.text,
          instruction?.description
        ], '')).trim())
        .filter(Boolean);
    },
    is_last_step() { return this.current_step >= this.intake_steps.length; },
  submit_button_label() { return this.is_last_step ? 'Save form' : 'Continue'; },
  has_multiple_forms() { return this.application_forms.length > 1; },
  can_submit_current_form() {
    return this.allowed_actions.length > 0 || this.intake_steps.some((main_section) => (
      Array.isArray(main_section?.sub_sections) && main_section.sub_sections.some((sub_section) => this.can_edit_sub_section(sub_section))
    ));
  }
};
